"use client";

import Link from "next/link";
import { Languages, X } from "lucide-react";
import { usePathname, useSearchParams } from "next/navigation";
import { useEffect, useState } from "react";
import {
  browserLanguageValues,
  localizedMarketingPath,
  marketingLocaleFromPath,
  MARKETING_LOCALE_DISMISSAL_KEY,
  MARKETING_LOCALE_COOKIE,
  preferredMarketingLocale,
  type MarketingLocale,
} from "@/lib/i18n/marketingLocale";

type Suggestion = {
  current: MarketingLocale;
  preferred: MarketingLocale;
  href: string;
};

function readDismissal(): string | null {
  try {
    return window.localStorage.getItem(MARKETING_LOCALE_DISMISSAL_KEY);
  } catch {
    return null;
  }
}

function writeDismissal(value: string) {
  try {
    window.localStorage.setItem(MARKETING_LOCALE_DISMISSAL_KEY, value);
  } catch {
    // Storage can be disabled. The selector remains available.
  }
}

function hasExplicitLocalePreference(): boolean {
  return document.cookie.split(";").some((part) =>
    part.trim().startsWith(`${MARKETING_LOCALE_COOKIE}=`),
  );
}

export function MarketingLanguageSuggestion() {
  const pathname = usePathname() || "/";
  const searchParams = useSearchParams();
  const [suggestion, setSuggestion] = useState<Suggestion | null>(null);

  useEffect(() => {
    const suffix = searchParams.toString() ? `?${searchParams.toString()}` : "";
    const currentPath = `${pathname}${suffix}`;
    const current = marketingLocaleFromPath(pathname);
    if (!current) return;
    if (hasExplicitLocalePreference()) return;
    const preferred = preferredMarketingLocale(browserLanguageValues());
    if (preferred === current) return;
    const dismissalValue = `${current}:${preferred}`;
    if (readDismissal() === dismissalValue) return;
    setSuggestion({ current, preferred, href: localizedMarketingPath(currentPath, preferred) });
  }, [pathname, searchParams]);

  if (!suggestion) return null;

  const suggestionCopy: Record<MarketingLocale, { title: string; body: string; primary: string }> = {
    en: {
      title: "Prefer English?",
      body: "You can view Novilot, its tools, and its guides in English.",
      primary: "View in English",
    },
    ar: {
      title: "تفضّل التصفح بالعربية؟",
      body: "يمكنك عرض Novilot وصفحات الأدوات والأدلة باللغة العربية.",
      primary: "عرض الصفحة بالعربية",
    },
    zh: {
      title: "更喜欢简体中文？",
      body: "你可以用简体中文查看 Novilot、工具和指南。",
      primary: "以简体中文查看",
    },
    "zh-tw": {
      title: "更喜歡繁體中文？",
      body: "你可以用繁體中文查看 Novilot、工具和指南。",
      primary: "以繁體中文查看",
    },
    es: {
      title: "¿Prefieres español?",
      body: "Puedes ver Novilot, sus herramientas y sus guías en español.",
      primary: "Ver en español",
    },
    de: {
      title: "Bevorzugst du Deutsch?",
      body: "Du kannst Novilot, seine Tools und Anleitungen auf Deutsch ansehen.",
      primary: "Auf Deutsch ansehen",
    },
    fr: {
      title: "Vous préférez le français ?",
      body: "Vous pouvez consulter Novilot, ses outils et ses guides en français.",
      primary: "Voir en français",
    },
    pt: {
      title: "Prefere português?",
      body: "Você pode ver o Novilot, suas ferramentas e seus guias em português.",
      primary: "Ver em português",
    },
    hi: {
      title: "क्या आप हिन्दी पसंद करते हैं?",
      body: "आप Novilot, इसके टूल और गाइड हिन्दी में देख सकते हैं।",
      primary: "हिन्दी में देखें",
    },
    ko: {
      title: "한국어를 선호하시나요?",
      body: "Novilot과 도구, 가이드를 한국어로 볼 수 있습니다.",
      primary: "한국어로 보기",
    },
    it: {
      title: "Preferisci l’italiano?",
      body: "Puoi vedere Novilot, gli strumenti e le guide in italiano.",
      primary: "Vedi in italiano",
    },
    ru: {
      title: "Предпочитаете русский?",
      body: "Novilot, инструменты и руководства можно смотреть на русском.",
      primary: "Смотреть на русском",
    },
    vi: {
      title: "Bạn thích Tiếng Việt?",
      body: "Bạn có thể xem Novilot, công cụ và hướng dẫn bằng Tiếng Việt.",
      primary: "Xem bằng Tiếng Việt",
    },
    ja: {
      title: "日本語を希望しますか？",
      body: "Novilot、ツール、ガイドを日本語で見られます。",
      primary: "日本語で見る",
    },
    nl: {
      title: "Liever Nederlands?",
      body: "Je kunt Novilot, de tools en de gidsen in het Nederlands bekijken.",
      primary: "Bekijk in het Nederlands",
    },
    pl: {
      title: "Wolisz polski?",
      body: "Możesz oglądać Novilot, narzędzia i poradniki po polsku.",
      primary: "Zobacz po polsku",
    },
    id: {
      title: "Lebih suka Bahasa Indonesia?",
      body: "Anda dapat melihat Novilot, alat, dan panduan dalam Bahasa Indonesia.",
      primary: "Lihat dalam Bahasa Indonesia",
    },
    ro: {
      title: "Preferi româna?",
      body: "Poți vedea Novilot, uneltele și ghidurile în română.",
      primary: "Vezi în română",
    },
  };
  const copy = suggestionCopy[suggestion.preferred];
  const stay = suggestion.current === "ar"
    ? "البقاء بالعربية"
    : suggestion.current === "zh"
      ? "留在简体中文"
      : suggestion.current === "zh-tw"
        ? "留在繁體中文"
        : suggestion.current === "es"
          ? "Seguir en español"
          : suggestion.current === "de"
            ? "Auf Deutsch bleiben"
            : suggestion.current === "fr"
              ? "Rester en français"
              : suggestion.current === "pt"
                ? "Continuar em português"
                : suggestion.current === "hi"
                  ? "हिन्दी में बने रहें"
                  : suggestion.current === "ko"
                    ? "한국어 유지"
                    : suggestion.current === "it"
                      ? "Resta in italiano"
                      : suggestion.current === "ru"
                        ? "Остаться на русском"
                        : suggestion.current === "vi"
                          ? "Ở lại Tiếng Việt"
                          : suggestion.current === "ja"
                            ? "日本語のまま"
                            : suggestion.current === "nl"
                              ? "Blijf bij Nederlands"
                              : suggestion.current === "pl"
                                ? "Zostań przy polskim"
                                : suggestion.current === "id"
                                  ? "Tetap di Bahasa Indonesia"
                                  : suggestion.current === "ro"
                                    ? "Rămâi în română"
                : "Stay in English";
  const dismissalValue = `${suggestion.current}:${suggestion.preferred}`;

  return (
    <aside
      role="status"
      dir={suggestion.preferred === "ar" ? "rtl" : "ltr"}
      className="mx-auto mt-4 flex w-[calc(100%-2rem)] max-w-6xl items-center gap-3 rounded-xl border border-border bg-muted/80 px-4 py-3 text-sm shadow-sm"
      data-i18n-skip
    >
      <Languages className="hidden h-4 w-4 shrink-0 text-accent sm:block" aria-hidden="true" />
      <div className="min-w-0 flex-1">
        <p className="font-semibold text-foreground">{copy.title}</p>
        <p className="mt-0.5 text-muted-foreground">{copy.body}</p>
      </div>
      <Link
        href={suggestion.href}
        hrefLang={suggestion.preferred}
        lang={suggestion.preferred}
        onClick={() => {
          document.cookie = `nc_marketing_locale=${suggestion.preferred}; Path=/; Max-Age=31536000; SameSite=Lax`;
          writeDismissal(dismissalValue);
        }}
        className="shrink-0 rounded-lg bg-accent px-3 py-2 font-medium text-accent-foreground hover:opacity-90"
      >
        {copy.primary}
      </Link>
      <button
        type="button"
        onClick={() => {
          writeDismissal(dismissalValue);
          setSuggestion(null);
        }}
        className="shrink-0 rounded-lg px-2 py-2 text-muted-foreground hover:bg-background hover:text-foreground"
        aria-label={stay}
        title={stay}
      >
        <X className="h-4 w-4" aria-hidden="true" />
      </button>
    </aside>
  );
}
